import { DEFAULT_PAGE_SIZE } from '@spx-portal/shared-utils';
import { FormInstance } from 'ssc-ui-react/typings/components/form/types';

import { createSelectors } from '@/store/constructor/createSelectors';
import { createStore } from '@/store/constructor/createStore';

import { getWorkstationPerformanceLastHour, GetWorkstationPerformanceLastHourParams } from '../api';
import { SorterEnum } from '../const';
import { WorkstationPerformanceLastHourItem, WorkstationPerformanceLastHourSorterModel } from '../defined';

type State = {
  loading: boolean;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
  };
  totalInfo: {
    below_target_count?: number;
    on_target_count?: number;
    total_count?: number;
  };
  items: WorkstationPerformanceLastHourItem[];
  sorterModel: WorkstationPerformanceLastHourSorterModel;
  lastHourForm?: FormInstance;
};

type Action = {
  getWorkstationPerformanceLastHourItems: () => WorkstationPerformanceLastHourItem[];
  getWorkstationPerformanceLastHourAsync: (params: GetWorkstationPerformanceLastHourParams) => Promise<void>;
  getSorterModel: () => WorkstationPerformanceLastHourSorterModel;
  setSorterModel: (sorterModel: WorkstationPerformanceLastHourSorterModel) => void;
  setLastHourForm: (lastHourForm?: FormInstance) => void;
  getLastHourForm: () => FormInstance | undefined;
  getTotalInfo: () => {
    below_target_count?: number;
    on_target_count?: number;
    total_count?: number;
  };
  setPagination: (pagination: { current: number; pageSize: number; total: number }) => void;
  getPagination: () => { current: number; pageSize: number; total: number };
};

const useWorkstationPerformanceStore = createSelectors(
  createStore<State & Action>((set, get) => {
    return {
      loading: false,
      lastHourForm: undefined,
      totalInfo: {
        below_target_count: 0,
        on_target_count: 0,
        total_count: 0,
      },
      pagination: {
        current: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        total: 0,
      },
      sorterModel: {
        hourly_productivity: undefined,
        manhour: undefined,
        target: undefined,
      },
      items: [],
      getTotalInfo: () => get().totalInfo,
      getSorterModel: () => get().sorterModel,
      setSorterModel: (sorterModel) => set({ sorterModel }),
      setLastHourForm: (lastHourForm) => set({ lastHourForm }),
      getLastHourForm: () => get().lastHourForm,
      getWorkstationPerformanceLastHourItems: () => get().items,
      setPagination: (pagination) => set({ pagination }),
      getPagination: () => get().pagination,
      getWorkstationPerformanceLastHourAsync: async (params) => {
        try {
          set({ loading: true });
          const sorterModel = get().sorterModel;
          const targetKey = Object.keys(sorterModel).find(key => sorterModel[key as keyof typeof sorterModel]);
          const ascKey = `${targetKey}_asc`;
          const descKey = `${targetKey}_desc`;
          const order = sorterModel[targetKey as keyof typeof sorterModel];
          const order_by_type = targetKey
            ? (
              order === 'ascend'
                ? SorterEnum[ascKey as keyof typeof SorterEnum]
              : order === 'descend' ? SorterEnum[descKey as keyof typeof SorterEnum] : undefined
            )
            : undefined;
          const defaultParams = {
            pageno: get().pagination.current,
            count: get().pagination.pageSize,
            order_by_type,
            select_meet_target: get().lastHourForm?.getFieldValue('select_meet_target'),
            biz_workstation_id_list: get().lastHourForm?.getFieldValue('biz_workstation_id_list'),
          };
          const res = await getWorkstationPerformanceLastHour({ ...defaultParams, ...params });
          const totalInfo = res.data?.total_info || {};
          set({
            pagination: {
              ...get().pagination,
              total: res.data?.total || 0,
              current: res.data?.pageno || 1,
            },
            items: res.data?.items || [],
            totalInfo: {
              below_target_count: totalInfo.below_target_count || 0,
              on_target_count: totalInfo.on_target_count || 0,
              total_count: totalInfo.total_count || 0,
            },
          });
        } catch (error) {
          console.error(error);
        } finally {
          set({ loading: false });
        }
      },
    };
}));

export default useWorkstationPerformanceStore;
